import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { TopMediaInfoService } from '../top-media-info/top-media-info.service';
import { TreeManagerService } from '../../../project-subsection/services/tree-manager.service';
import { SubsectionService } from '../../services/subsection.service';
import { ReleaseTreeService } from '../../../project-subsection/services/release-tree-service';
import { PermissionService } from '../../services/permissions.service';
import { ProjectService } from '../../services/project.service';
import { TranslateService } from '../../services/translate.service';
import { BaseTreeNode, TreeNodeType, TreeState } from '../../../project-subsection/models/tree-node';
import { ActiveSubsectionState, OptionVersionSimple } from '../../interfaces';
import { ReleaseTreeState } from '../../../project-subsection/models';

@Component({
  selector: 'mtm-version-dropdown',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule],
  templateUrl: './version-dropdown.component.html',
  styleUrl: './version-dropdown.component.scss',
})
export class VersionDropdownComponent implements OnInit, OnDestroy {
  private readonly treeManagerService = inject(TreeManagerService);
  private readonly subsectionService = inject(SubsectionService);
  private readonly releaseTreeService = inject(ReleaseTreeService);
  private readonly permissionService = inject(PermissionService);
  private readonly projectService = inject(ProjectService);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly translateService = inject(TranslateService);

  private readonly ngUnsubscribe = new Subject<void>();
  private releaseRecord: Record<string, any> = {};
  private isReleasePreview = false;
  private isSwitchingVersion = false;

  readonly treeNodeType = TreeNodeType;
  subSectionState: ActiveSubsectionState | null = null;
  isBusy = false;

  get treeState(): TreeState<BaseTreeNode> {
    return this.treeManagerService.getCurrentState();
  }

  get isFileNode(): boolean {
    return (
      this.treeState?.selectedNode?.type === TreeNodeType.File ||
      (this.isSwitchingVersion && this.treeState?.selectedNode.type === TreeNodeType.Version)
    );
  }

  get isVisible(): boolean {
    this.subSectionState?.activeOption?.simpleVersions;
    return this.topMediaInfoService.isVersionListVisible && this.isFileNode;
  }

  get hasEditPermission(): boolean {
    const projectId = this.projectService?.project?.id;
    if (!projectId) {
      return false;
    }
    return this.permissionService.canUpdateProject();
  }

  get hasLockedVersionInCurrentOption(): boolean {
    if (!this.subSectionState?.activeOption?.simpleVersions || !this.subSectionState?.activeOption?.optionId) {
      return false;
    }

    return this.subSectionState.activeOption.simpleVersions.some((version: OptionVersionSimple) => {
      const versionId = `${this.subSectionState?.activeOption?.optionId}-${version.versionNumber}`;
      return !!this.releaseRecord[versionId];
    });
  }

  get shouldDisableInteractiveElements(): boolean {
    return this.hasLockedVersionInCurrentOption || this.isReleasePreview;
  }

  translate(key: string): string {
    return this.translateService.data[key] || key;
  }

  ngOnInit(): void {
    this.releaseTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (state: ReleaseTreeState) => {
          this.releaseRecord = state?.releaseRecord || {};
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((subsectionState: ActiveSubsectionState | null) => !!subsectionState?.activeFile)
      )
      .subscribe((subsectionState: ActiveSubsectionState) => {
        this.subSectionState = subsectionState;
      });

    this.subsectionService.uiState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((state) => {
      this.isReleasePreview = !!state?.isReleasePreview;
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  switchVersion(versionNumber: number): void {
    const subsectionPreview = this.topMediaInfoService.getSubsectionPreview();
    subsectionPreview?.sliderMediaPlayerComponent?.switchVersion(versionNumber);

    // When switching version, the node temporarily becomes a Version type,
    // so we set isSwitching to true to keep the dropdown visible during the switch.
    this.isSwitchingVersion = true;
    setTimeout(() => {
      this.isSwitchingVersion = false;
    }, 1000);
  }

  showManageVersions(): void {
    const subsectionPreview = this.topMediaInfoService.getSubsectionPreview();
    subsectionPreview?.sliderMediaPlayerComponent?.showManageVersions();
  }

  createNewVersion(): void {
    const subsectionPreview = this.topMediaInfoService.getSubsectionPreview();
    subsectionPreview?.sliderMediaPlayerComponent?.createNewVersion();
  }
}
